import React from "react";
import {
  Box,
  Button,
  Heading,
  Text,
  VStack,
  HStack,
  Icon,
  useColorModeValue,
  Code,
  Divider,
  Container,
  Flex,
} from "@chakra-ui/react";
import { FiAlertTriangle, FiRefreshCw, FiHome } from "react-icons/fi";
import bgBody from "assets/img/background-body-admin.png";
import Card from "components/Card/Card.js";
import CardBody from "components/Card/CardBody.js";
import CardHeader from "components/Card/CardHeader.js";

// Beautiful Error Display Component matching dashboard style
const ErrorDisplay = ({ error, errorInfo }) => {
  const titleColor = "white";
  const textColor = "white";
  const headingColor = "#FC8181"; // Bright red for better visibility
  const iconColor = "#FC8181"; // Bright red for better visibility
  const codeBg = useColorModeValue("rgba(19,21,54,0.8)", "rgba(0,0,0,0.6)");
  const codeTextColor = "#FC8181"; // Bright red for code text

  return (
    <Box
      minH="100vh"
      bgImage={bgBody}
      bgSize="cover"
      bgPosition="center center"
      bgRepeat="no-repeat"
      display="flex"
      alignItems="center"
      justifyContent="center"
      p={4}
      position="relative"
    >
      {/* Overlay for better readability */}
      <Box
        position="absolute"
        top={0}
        left={0}
        right={0}
        bottom={0}
        bg="rgba(0, 0, 0, 0.6)"
        zIndex={0}
      />

      <Container maxW="2xl" position="relative" zIndex={1}>
        <Card
          p="0px"
          borderRadius="20px"
          background="rgba(19,21,56,0.85)"
          boxShadow="0px 0px 2px 2px rgba(255,255,255,0.2), 0px 10px 40px rgba(0,0,0,0.5)"
        >
          <CardHeader
            p="30px 40px"
            borderBottom="1px solid rgba(255,255,255,0.2)"
          >
            <Flex justify="center" align="center">
              {/* Icon with animation */}
              <Box position="relative">
                <Box
                  position="absolute"
                  inset={0}
                  bg={iconColor}
                  opacity={0.2}
                  borderRadius="full"
                  transform="scale(1.8)"
                  animation="pulse 2s infinite"
                />
                <Icon
                  as={FiAlertTriangle}
                  w={20}
                  h={20}
                  color={iconColor}
                  position="relative"
                  zIndex={1}
                />
              </Box>
            </Flex>
          </CardHeader>

          <CardBody p="40px">
            <VStack spacing={6} textAlign="center">
              {/* Heading */}
              <VStack spacing={3}>
                <Heading
                  size="xl"
                  color={headingColor}
                  fontWeight="bold"
                  letterSpacing="tight"
                >
                  Something Went Wrong
                </Heading>
                <Text
                  fontSize="md"
                  color={textColor}
                  maxW="md"
                  lineHeight="tall"
                  fontWeight="300"
                  opacity={0.95}
                >
                  We encountered an unexpected error. Don't worry, your data is
                  safe. Please try refreshing the page or return to the home
                  page.
                </Text>
              </VStack>

              {/* Action Buttons */}
              <HStack spacing={4} mt={4} flexWrap="wrap" justify="center">
                <Button
                  leftIcon={<FiRefreshCw />}
                  variant="brand"
                  size="lg"
                  onClick={() => window.location.reload()}
                  px={8}
                  _hover={{
                    transform: "translateY(-2px)",
                    boxShadow: "0px 8px 16px rgba(0,0,0,0.3)",
                  }}
                  transition="all 0.2s"
                >
                  Refresh Page
                </Button>
                <Button
                  leftIcon={<FiHome />}
                  variant="outline"
                  size="lg"
                  borderColor="rgba(255,255,255,0.5)"
                  color="white"
                  _hover={{
                    bg: "rgba(255,255,255,0.15)",
                    borderColor: "rgba(255,255,255,0.7)",
                    color: "white",
                    transform: "translateY(-2px)",
                  }}
                  onClick={() => {
                    window.location.href = "/PapercableNew";
                  }}
                  px={8}
                  transition="all 0.2s"
                >
                  Go Home
                </Button>
              </HStack>

              {/* Error Details (Development Only) */}
              {process.env.NODE_ENV === "development" && (error || errorInfo) && (
                <>
                  <Divider my={6} borderColor="rgba(255,255,255,0.2)" />
                  <Box
                    w="100%"
                    textAlign="left"
                    bg={codeBg}
                    borderRadius="lg"
                    p={6}
                    border="1px solid rgba(255,255,255,0.2)"
                  >
                    <Heading
                      size="sm"
                      color={headingColor}
                      mb={4}
                      fontWeight="bold"
                    >
                      Error Details
                    </Heading>
                    {error && (
                      <Box mb={4}>
                        <Text
                          fontSize="xs"
                          color="white"
                          mb={2}
                          fontWeight="semibold"
                          opacity={0.9}
                        >
                          Error Message:
                        </Text>
                        <Code
                          display="block"
                          whiteSpace="pre-wrap"
                          p={3}
                          bg="rgba(0,0,0,0.5)"
                          color={codeTextColor}
                          borderRadius="md"
                          fontSize="xs"
                          overflowX="auto"
                          border="1px solid rgba(255,255,255,0.2)"
                          fontWeight="500"
                        >
                          {error.toString()}
                        </Code>
                      </Box>
                    )}
                    {errorInfo && errorInfo.componentStack && (
                      <Box>
                        <Text
                          fontSize="xs"
                          color="white"
                          mb={2}
                          fontWeight="semibold"
                          opacity={0.9}
                        >
                          Component Stack:
                        </Text>
                        <Code
                          display="block"
                          whiteSpace="pre-wrap"
                          p={3}
                          bg="rgba(0,0,0,0.5)"
                          color={codeTextColor}
                          borderRadius="md"
                          fontSize="xs"
                          maxH="200px"
                          overflowY="auto"
                          border="1px solid rgba(255,255,255,0.2)"
                          fontWeight="500"
                        >
                          {errorInfo.componentStack}
                        </Code>
                      </Box>
                    )}
                  </Box>
                </>
              )}
            </VStack>
          </CardBody>
        </Card>
      </Container>

      <style>
        {`
          @keyframes pulse {
            0%, 100% {
              opacity: 0.2;
              transform: scale(1.8);
            }
            50% {
              opacity: 0.3;
              transform: scale(1.9);
            }
          }
        `}
      </style>
    </Box>
  );
};

// Error Boundary Class Component
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({
      error: error,
      errorInfo: errorInfo,
    });
    // You can also log the error to an error reporting service here
    console.error("Error caught by boundary:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <ErrorDisplay
          error={this.state.error}
          errorInfo={this.state.errorInfo}
        />
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
